import React from 'react';
import { User, Heart } from 'lucide-react';
import { Link } from 'react-router-dom';

export type Character = {
  id: string;
  name: string;
  role: string;
  description: string;
  avatar_url?: string;
  favorite_count: number;
  has_favorited: boolean;
  idea_id: string;
};

type CharacterCardProps = {
  character: Character;
  onToggleFavorite: (characterId: string) => Promise<void>;
};

export default function CharacterCard({ character, onToggleFavorite }: CharacterCardProps) {
  const [isFavoriting, setIsFavoriting] = React.useState(false);

  const handleFavorite = async (e: React.MouseEvent) => {
    e.preventDefault(); // Prevent link navigation when clicking favorite
    try {
      setIsFavoriting(true);
      await onToggleFavorite(character.id);
    } finally {
      setIsFavoriting(false);
    }
  };

  return (
    <Link 
      to={`/idea/${character.idea_id}/character/${character.id}`}
      className="block bg-white rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow"
    >
      <div className="flex items-start gap-3">
        <div className="w-12 flex-shrink-0">
          {character.avatar_url ? (
            <div className="w-12 h-12 rounded-full overflow-hidden">
              <img
                src={character.avatar_url}
                alt={character.name}
                className="w-full h-full object-cover"
              />
            </div>
          ) : (
            <div 
              className="w-12 h-12 rounded-full flex items-center justify-center"
              style={{ backgroundColor: 'var(--color-accent3)' }}
            >
              <User 
                className="w-6 h-6" 
                style={{ color: 'var(--color-accent1)' }}
              />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h3 className="font-medium" style={{ color: 'var(--color-font)' }}>{character.name}</h3>
              <p className="text-sm" style={{ color: 'var(--color-accent1)' }}>{character.role}</p>
            </div>
            <button
              onClick={handleFavorite}
              disabled={isFavoriting}
              className={`p-1 rounded-full transition-colors ${
                character.has_favorited
                  ? 'text-red-500 hover:bg-red-50'
                  : 'text-gray-400 hover:text-red-500 hover:bg-red-50'
              }`}
            >
              <Heart className="w-5 h-5" fill={character.has_favorited ? "currentColor" : "none"} />
            </button>
          </div>
          <p 
            className="mt-2 text-sm line-clamp-2"
            style={{ color: 'var(--color-font)' }}
          >
            {character.description}
          </p>
          <div 
            className="mt-2 text-xs"
            style={{ color: 'var(--color-accent2)' }}
          >
            {character.favorite_count} {character.favorite_count === 1 ? 'favorite' : 'favorites'}
          </div>
        </div>
      </div>
    </Link>
  );
}